import React, { useState } from 'react';
import { Platform, StyleSheet, Switch, Text, View } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Button } from '@/components/residents/marketplace/shared/Button';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import {
  REDESIGN_SPACING,
  REDESIGN_TYPOGRAPHY,
} from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import type { SurveySummary } from '@/types/residents/corkBoard';

interface Props {
  locked: boolean;
  survey: SurveySummary;
  onConfigChange: (patch: { expiresAt?: string | null; allowReplies?: boolean }) => void;
  /** When true, settings controls are read-only (e.g. survey already closed). */
  disabled?: boolean;
}

export const SurveyEditPanel: React.FC<Props> = ({
  locked,
  survey,
  onConfigChange,
  disabled,
}) => {
  const { colors, components } = useThemeColors();
  const [showPicker, setShowPicker] = useState(false);

  return (
    <GlassCard
      variant="soft"
      borderRadius={REDESIGN_SPACING.cardBorderRadius}
      padding={REDESIGN_SPACING.cardPadding}
      style={styles.spacing}
    >
      <Text
        style={[
          REDESIGN_TYPOGRAPHY.sectionTitle,
          { color: colors.text.primary, marginBottom: REDESIGN_SPACING.sectionGap },
        ]}
      >
        Survey settings
      </Text>

      {locked && (
        <View
          style={[
            styles.lockBanner,
            { backgroundColor: colors.accent.redBg, borderColor: colors.accent.red },
          ]}
        >
          <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.accent.red }]}>
            Locked: responses already submitted. Questions and anonymity can no longer change.
          </Text>
        </View>
      )}

      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.primary }]}>
            Discussion replies
          </Text>
          <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.secondary }]}>
            {survey.allowReplies
              ? 'Residents can reply on this thread.'
              : 'Replies are off.'}
          </Text>
        </View>
        <Switch
          value={survey.allowReplies}
          onValueChange={(v) => onConfigChange({ allowReplies: v })}
          disabled={disabled}
          trackColor={{ false: components.toggle.trackOff, true: components.toggle.trackOn }}
          thumbColor={components.toggle.thumb}
        />
      </View>

      <View style={[styles.divider, { backgroundColor: colors.card.border }]} />

      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.primary }]}>
            Expiration
          </Text>
          <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.secondary }]}>
            {survey.expiresAt
              ? new Date(survey.expiresAt).toLocaleString()
              : 'No expiration'}
          </Text>
        </View>
        <Button
          title={survey.expiresAt ? 'Change' : 'Set'}
          variant="outline"
          size="small"
          disabled={disabled}
          onPress={() => setShowPicker(true)}
        />
      </View>
      {showPicker && (
        <DateTimePicker
          value={
            survey.expiresAt
              ? new Date(survey.expiresAt)
              : new Date(Date.now() + 24 * 3600 * 1000)
          }
          mode="datetime"
          minimumDate={new Date()}
          onChange={(_e, date) => {
            if (Platform.OS === 'android') setShowPicker(false);
            if (date) onConfigChange({ expiresAt: date.toISOString() });
          }}
        />
      )}
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  spacing: { marginVertical: REDESIGN_SPACING.sectionGap },
  lockBanner: {
    borderWidth: 1,
    borderRadius: REDESIGN_SPACING.buttonRadius,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: REDESIGN_SPACING.sectionGap,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: REDESIGN_SPACING.sectionGap,
    gap: 12,
  },
  rowText: { flex: 1, gap: 2 },
  divider: { height: StyleSheet.hairlineWidth, marginVertical: 4 },
});
